import React, { useEffect, useState } from 'react';
import { Button, Title } from '@gerdau/hefesto';
import { DateField } from '@gerdau/hefesto-mais-web/DateField';
import { DateRangeType } from '@gerdau/hefesto-mais-web/DateField/DateField.types';
import moment from 'moment';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import FabricationMultipleContractsSelect from '../../../components/FabricationMultipleContractsSelect';
import IsManagerFabricationContractInput from '../../../components/IsManagerFabricationContractInput';
import { ERoutes } from '../../../routes';
import { ApplicationState } from '../../../store';
import { loadContractsV2 } from '../../../store/ducks/fabrication-contracts/actions';
import { loadFabricationOrders } from '../../../store/ducks/fabrication-orders/actions';
import { IFabricationOrder } from '../../../store/ducks/fabrication-orders/types';
import { updateFabricationPurchaseOrderSettings } from '../../../store/ducks/fabrication-purchase-order/actions';
import { sendTagging } from '../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../utils/Tagging';
import { textEllipsis } from '../../../utils/formatStrings';
import { getFabricationPurchaseStateFromFabricationOrder } from '../../../utils/purchaseHelpers';
import { EPurchaseRoutes } from '../../Purchase/purchase.routes';
import DeliveriesTable from './DeliveriesTable';
import FabricationOrderDetails from './FabricationOrderDetails';
import FabricationOrdersFilters from './FabricationOrdersFilters';
import FabricationOrdersTable from './FabricationOrdersTable/index';
import {
  Container,
  DefaultMessage,
  FabricationOrdersFilter,
  FabricationOrdersHeader,
  SkeletonContainer
} from './styles';

export type TFabricationView = {
  view: 'fabricationTable' | 'details' | 'deliveries';
  data?: IFabricationOrder;
};

const FabricationOrders: React.FC = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  const fabricationOrdersState = useSelector(
    (state: ApplicationState) => state.fabricationOrders
  );

  const contractsState = useSelector(
    (appState: ApplicationState) => appState.fabricationContracts
  );

  const isManager = useSelector(
    (appState: ApplicationState) => appState.user.isManager
  );

  const [filteredOrders, setFilteredOrders] = useState<IFabricationOrder[]>(
    fabricationOrdersState.fabricationOrders || []
  );

  const [selectedContractNumbers, setSelectedContractNumbers] = useState(
    fabricationOrdersState.selectedContractNumbers
  );

  const [startSelectedDate, setStartSelectedDate] = useState<moment.Moment>(
    fabricationOrdersState.selectedStartDate
  );
  const [endSelectedDate, setEndSelectedDate] = useState<moment.Moment>(
    fabricationOrdersState.selectedEndDate
  );

  const currentDate = moment().utcOffset(-3).toDate();
  const [maxDate, setMaxDate] = useState<Date>(currentDate);

  const [dateSelectedRange, setDateSelectedRange] = useState<DateRangeType>([
    moment(startSelectedDate).toDate(),
    moment(endSelectedDate).toDate()
  ]);

  const [activeView, setActiveView] = useState<TFabricationView>({
    view: 'fabricationTable'
  });

  useEffect(() => {
    window.scrollTo({ behavior: 'smooth', top: 0 });
  }, [activeView]);

  const onBuyAgainClick = (fabricationOrder: IFabricationOrder) => {
    const fabricationPurchaseOrderState =
      getFabricationPurchaseStateFromFabricationOrder(fabricationOrder);
    dispatch(
      updateFabricationPurchaseOrderSettings(fabricationPurchaseOrderState)
    );
    goToPurchaseFabrication();
  };

  const goToPurchaseFabrication = () => {
    history.push(`${ERoutes.PURCHASE}/${EPurchaseRoutes.FABRICATION}`);
  };

  useEffect(() => {
    if (
      !isManager &&
      contractsState.contracts === undefined &&
      !contractsState.loading &&
      !contractsState.error
    ) {
      dispatch(loadContractsV2());
    }
  });

  useEffect(() => {
    if (fabricationOrdersState.fabricationOrders) {
      setFilteredOrders(fabricationOrdersState.fabricationOrders);
    }
  }, [fabricationOrdersState.fabricationOrders]);

  const handleLoadFabricationOrders = () => {
    const needToLoadContract =
      isManager &&
      !contractsState.contracts?.find((c) =>
        selectedContractNumbers.includes(c.contractNumber)
      );

    dispatch(
      loadFabricationOrders(
        selectedContractNumbers,
        startSelectedDate,
        endSelectedDate,
        needToLoadContract
      )
    );
  };

  const getCustomerOrderText = (): string | undefined => {
    if (
      selectedContractNumbers.length < 6 ||
      fabricationOrdersState.loading ||
      !contractsState.contracts ||
      contractsState.contracts.length === 0
    ) {
      return undefined;
    } else {
      const contract = contractsState.contracts?.find((cont) =>
        selectedContractNumbers.includes(cont.contractNumber)
      );
      return (
        contract &&
        (`${contract.contractNumber} - ${textEllipsis(
          contract.customerOrder,
          24
        )}` ||
          undefined)
      );
    }
  };

  const { screenName = '', itemDescription = '' } = getTaggingInformationScreen(
    pathname,
    true
  );

  useEffect(() => {
    dispatch(
      sendTagging({
        event: 'page_view',
        page: pathname,
        screenName
      })
    );
  }, []);

  const getSelectedContractCustomerId = (contractNumber: string) => {
    const contract = contractsState.contracts?.find(
      (cont) => cont.contractNumber === contractNumber
    );
    return contract?.soldTo.code || '';
  };

  return (
    <>
      {activeView.view === 'fabricationTable' && (
        <>
          <FabricationOrdersHeader>
            <Title level={1}>Meus pedidos</Title>
            <span>
              Selecione o contrato e o período para exibir os resultados.
            </span>
            <FabricationOrdersFilter>
              {isManager && (
                <IsManagerFabricationContractInput
                  title="Contrato"
                  loading={fabricationOrdersState.loading}
                  contractError={fabricationOrdersState.contractError || false}
                  contractNumber={selectedContractNumbers.join(',')}
                  setContractNumber={(contract) =>
                    setSelectedContractNumbers(contract.split(','))
                  }
                  customerOrder={getCustomerOrderText()}
                  helperText="Você pode pesquisar mais de um contrato"
                />
              )}

              {!isManager && (
                <FabricationMultipleContractsSelect
                  placeholder="Selecione um contrato"
                  title="Contrato"
                  contracts={contractsState.contracts || []}
                  loading={
                    contractsState.loading || fabricationOrdersState.loading
                  }
                  onChange={(value) => {
                    value && setSelectedContractNumbers(value as string[]);
                  }}
                  selectedContractNumbers={selectedContractNumbers}
                  showClosedContractMessage
                />
              )}

              <DateField
                label="Data de envio do pedido"
                name="dataPicker"
                helperText="A data de envio é o dia da sua solicitação. A Diferença entre as datas não pode ser maior do que 6 meses."
                onChange={(date: DateRangeType) => {
                  setDateSelectedRange(date);
                  setStartSelectedDate(moment(date?.[0]));
                  const preMaxDate = moment(date?.[0])
                    .utcOffset(-3)
                    .add(6, 'months')
                    .toDate();
                  const endDate = moment(date?.[1]);
                  const changedEndDate =
                    date?.[1] &&
                    endDate.toString() !== endSelectedDate.toString();
                  setMaxDate(
                    changedEndDate
                      ? currentDate
                      : currentDate > preMaxDate
                      ? preMaxDate
                      : currentDate
                  );
                  setEndSelectedDate(moment(date?.[1]));
                }}
                selectsRange
                range={dateSelectedRange}
                allowPartialRange={true}
                maxDate={maxDate}
                disabled={
                  contractsState.loading || fabricationOrdersState.loading
                }
              ></DateField>

              <Button
                name="Search"
                variant="primary"
                onClick={() => {
                  dispatch(
                    sendTagging({
                      event: 'click',
                      screenName,
                      itemName: 'button:buscar',
                      itemDescription: `${itemDescription}buscar`,
                      filterInfo: `#cttObra:[${selectedContractNumbers}]#dtInicio:[${startSelectedDate}]#dtFim:[${endSelectedDate}]`
                    })
                  );
                  handleLoadFabricationOrders();
                }}
                loading={
                  contractsState.loading || fabricationOrdersState.loading
                }
                disabled={
                  contractsState.loading ||
                  fabricationOrdersState.loading ||
                  selectedContractNumbers.length === 0 ||
                  !startSelectedDate ||
                  !endSelectedDate
                }
              >
                Buscar
              </Button>
            </FabricationOrdersFilter>
          </FabricationOrdersHeader>

          <Container>
            {fabricationOrdersState.selectedContractNumbers.length === 0 ? (
              <DefaultMessage>
                <span>
                  Para ver seus pedidos,{' '}
                  <strong>
                    selecione um contrato de obra e o período desejado.
                  </strong>
                </span>
              </DefaultMessage>
            ) : (
              <FabricationOrdersFilters
                orders={fabricationOrdersState.fabricationOrders || []}
                setFilteredOrders={setFilteredOrders}
                onCleanFilters={() =>
                  setFilteredOrders(
                    fabricationOrdersState.fabricationOrders || []
                  )
                }
                isAllDisabled={
                  fabricationOrdersState.loading ||
                  fabricationOrdersState.error ||
                  (fabricationOrdersState.fabricationOrders !== undefined &&
                    fabricationOrdersState.fabricationOrders.length === 0)
                }
              />
            )}
            {fabricationOrdersState.selectedContractNumbers.length > 0 &&
              !fabricationOrdersState.loading &&
              !fabricationOrdersState.error && (
                <FabricationOrdersTable
                  fabricationOrders={filteredOrders}
                  setActiveView={setActiveView}
                />
              )}
            {fabricationOrdersState.loading && (
              <SkeletonContainer>
                <Skeleton count={6} />
              </SkeletonContainer>
            )}
          </Container>
        </>
      )}
      {activeView.view === 'details' && activeView.data && (
        <FabricationOrderDetails
          setActiveView={setActiveView}
          fabricationOrder={activeView.data}
          goToPurchaseFabrication={goToPurchaseFabrication}
          onBuyAgainClick={onBuyAgainClick}
        />
      )}
      {activeView.view === 'deliveries' && activeView.data && (
        <DeliveriesTable
          setActiveView={setActiveView}
          deliveries={activeView.data.deliveries}
          fabricationOrderId={activeView.data.id}
          contractNumber={activeView.data.contractNumber}
          customerId={getSelectedContractCustomerId(
            activeView.data.contractNumber
          )}
        ></DeliveriesTable>
      )}
    </>
  );
};

export default FabricationOrders;
